// src/views/UserViews/insights/components/UtilizationSection.tsx
//
// Stacks 4 cards in the narrow (1fr) column of Insights.tsx's main row. Each card's own
// component documents why it exists - see MetricCard.tsx, UtilizationProgressCard.tsx, and
// CoreProComparisonCard.tsx's own header comments for the "why a new component" reasoning.
//
//  1. Protected hours - `getProtectedHours` (InsightsDAO), rendered `format="count"` (not
//     percent - SafetySection owns that ratio framing).
//  2. Device utilization - `UtilizationProgressCard`, numerator/denominator against
//     `totalReportingDevices` (reused from Insights.tsx, never refetched).
//  3. Time muted - ported from the now-deleted SafetySection.tsx; see the JSX comment below.
//  4. Core vs. Pro Devices - `getCoreProDeviceCounts` (InsightsDAO); see that function's own
//     header for grain and undetermined-class handling.
//
// Labeling constraints (do not violate - see docs/guides/navigator-analytics-catalog.md
// "Never say" table):
//  - "device reporting hours", never "work hours" - measures Compass powered-on-and-paired
//    time, not scheduled shift time.
//  - "Reported this period"/"reporting devices", never "Worn" - nothing here measures on-body
//    wear.
//  - "no telemetry in 14 days", never "no session" - no session-tracking data exists in this
//    pipeline. OVERRIDE (confirmed twice): `insights_no_telemetry_14_days`'s display copy
//    says "no session" anyway - a copy-only exception, same category as "Protected Work
//    Hours" above.
//  - "Time muted", never "alerts ignored/dismissed" - `is_muted` is a sampled state, not an
//    event.

import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { StringKeys } from '@/resources/strings/StringKeys';
import type { InsightsSectionState } from '@/hooks/insights/useInsightsData';
import type {
  ComplianceTrendResult,
  CoreProDeviceCountSummary,
  ProtectedHoursSummary,
  UtilizationSummary,
} from '@/models/Insights/InsightsTypes';
import MetricCard from './MetricCard';
import UtilizationProgressCard from './UtilizationProgressCard';
import CoreProComparisonCard from './CoreProComparisonCard';

type UtilizationSectionProps = {
  protectedHours: InsightsSectionState<ProtectedHoursSummary>;
  utilization: InsightsSectionState<UtilizationSummary>;
  /** Reused from Insights.tsx (same object passed to ComplianceTrendChart) for Time muted's
   * `headlineMuteRatio` - never refetched. */
  complianceTrend: InsightsSectionState<ComplianceTrendResult>;
  /** `FleetStatus.totalReportingDevices`, reused - never re-queried. */
  totalReportingDevices: number | null;
  /** Card 4's own `getCoreProDeviceCounts` fetch - a separate Cube query from
   * `complianceTrend` above. */
  coreProDeviceCounts: InsightsSectionState<CoreProDeviceCountSummary>;
};

/** `Math.round(null)` coerces to `0` in JS, which would violate Rule 1 (never coerce a
 * null "not enough data" value into a real number) - this guards that explicitly. */
function roundOrNull(value: number | null): number | null {
  return value === null ? null : Math.round(value);
}

// Ported from the now-deleted SafetySection.tsx - provisional evenly-split thresholds (no
// real mute-rate distribution data exists yet), same treatment as FeatureComplianceSection's
// FEATURE_COMPLIANCE_WARNING_THRESHOLD.
const TIME_MUTED_WARNING_THRESHOLD = 1 / 3;
const TIME_MUTED_ERROR_THRESHOLD = 2 / 3;

/** `null` renders MetricCard's default uncolored em-dash. High-is-bad polarity (opposite
 * FeatureComplianceSection's scores): `>=` at both boundaries puts exactly 1/3 in the middle
 * band and exactly 2/3 in the top band. */
function valueColorForMuteRatio(ratio: number | null): string | undefined {
  if (ratio === null) return undefined;
  if (ratio >= TIME_MUTED_ERROR_THRESHOLD) return 'error.main';
  if (ratio >= TIME_MUTED_WARNING_THRESHOLD) return 'warning.main';
  return undefined; // bottom third: no tint (not success.main) - a low mute rate isn't celebrated
}

// Column-height tradeoff with ComplianceTrendChart - see Insights.tsx's main-row comment for
// the grid-level mechanics. This slot has no `height`, so cards 1-3 keep their own natural
// content height when Insights.tsx's grid stretches this section's root `Box`; `flexShrink: 0`
// (overriding flexbox's default of `1`) keeps them from being compressed below that height
// in the reverse scenario.
const CARD_SLOT_SX = { flexGrow: 0, flexShrink: 0 };

// Card 4's slot: `flexGrow: 1` makes it the one card that absorbs any leftover stretched
// height, so its own bordered `CoreProComparisonCard` - not an invisible spacer - reaches the
// compliance card's bottom (verified in InsightsMainRowAlignment.test.tsx). Chosen because
// it's the last card in the stack and the only one safe to adapt internally - see
// CoreProComparisonCard.tsx's "Height-growth handling" comment. `flexShrink: 0` keeps its own
// `CARD_MIN_HEIGHT` floor.
const GROWING_CARD_SLOT_SX = { flexGrow: 1, flexShrink: 0 };

export default function UtilizationSection({
  protectedHours,
  utilization,
  complianceTrend,
  totalReportingDevices,
  coreProDeviceCounts,
}: UtilizationSectionProps) {
  const { t } = useTranslation();

  const validHoursRounded = roundOrNull(protectedHours.data?.validHours ?? null);
  const protectedHoursValue = roundOrNull(protectedHours.data?.protectedHours ?? null);
  const protectedHoursCaption =
    validHoursRounded !== null
      ? t(StringKeys.INSIGHTS_PROTECTED_HOURS_CAPTION, {
          hours: validHoursRounded.toLocaleString(),
        })
      : undefined;

  return (
    <Box
      sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
      data-testid="utilization-section-root"
    >
      <Box sx={CARD_SLOT_SX}>
        <MetricCard
          testId="utilization-protected-hours"
          label={t(StringKeys.INSIGHTS_PROTECTED_HOURS_LABEL)}
          caption={protectedHoursCaption}
          tooltip={t(StringKeys.INSIGHTS_PROTECTED_HOURS_TOOLTIP)}
          value={protectedHoursValue}
          format="count"
          loading={protectedHours.loading}
          error={protectedHours.error}
          tone="positive"
          // Exception to the hover-strip default (see MetricCard.tsx's `revealOnHover`
          // comment) - caption stays permanently visible with its own info-icon tooltip.
          revealOnHover={false}
        />
      </Box>
      <Box sx={CARD_SLOT_SX}>
        <UtilizationProgressCard
          testId="utilization-devices-reporting"
          label={t(StringKeys.INSIGHTS_DEVICE_UTILIZATION_LABEL)}
          tooltip={t(StringKeys.INSIGHTS_DEVICE_UTILIZATION_TOOLTIP)}
          numerator={utilization.data?.devicesReportingInWindow ?? null}
          denominator={totalReportingDevices}
          staleDeviceCount={utilization.data?.noTelemetryIn14Days ?? null}
          loading={utilization.loading}
          error={utilization.error}
          // Same exception as the protected-hours card above.
          revealOnHover={false}
        />
      </Box>
      <Box sx={CARD_SLOT_SX}>
        {/* Ported from the now-deleted SafetySection.tsx (labeling constraint above still
            applies). Uses MetricCard's default revealOnHover, unlike cards 1-2 above. No
            `caption` prop - `INSIGHTS_TIME_MUTED_TOOLTIP` stands alone since MetricCard's
            revealText merge falls through to `tooltip` alone once `caption` is omitted. */}
        <MetricCard
          testId="safety-time-muted"
          label={t(StringKeys.INSIGHTS_TIME_MUTED_LABEL)}
          tooltip={t(StringKeys.INSIGHTS_TIME_MUTED_TOOLTIP)}
          value={complianceTrend.data?.headlineMuteRatio ?? null}
          format="percent"
          loading={complianceTrend.loading}
          error={complianceTrend.error}
          tone="neutral"
          valueColor={valueColorForMuteRatio(complianceTrend.data?.headlineMuteRatio ?? null)}
        />
      </Box>
      <Box sx={GROWING_CARD_SLOT_SX}>
        <CoreProComparisonCard
          testId="utilization-core-pro-device-counts"
          label={t(StringKeys.INSIGHTS_CORE_PRO_DEVICE_COUNT_TITLE)}
          tooltip={t(StringKeys.INSIGHTS_CORE_PRO_DEVICE_COUNT_TOOLTIP)}
          // `?? 0` is a "not loaded yet" fallback, not a Rule 1 violation - both counts are
          // non-nullable once `data` is populated (see CoreProDeviceCountSummary's header).
          coreDeviceCount={coreProDeviceCounts.data?.coreDeviceCount ?? 0}
          proDeviceCount={coreProDeviceCounts.data?.proDeviceCount ?? 0}
          loading={coreProDeviceCounts.loading}
          error={coreProDeviceCounts.error}
        />
      </Box>
    </Box>
  );
}
